/**
 * PhotoProofThumb primitive — Plan 02-10 / UI-SPEC §"<PhotoProofThumb>".
 *
 * Square 88×88 (mobile) / 120×120 (tablet) thumbnail with lazy-loaded,
 * async-decoded image. Tap opens an in-route lightbox via the onOpen
 * callback (the lightbox itself lives in the consuming feature — Plan
 * 10 ships the thumb only and ApprovalCard owns the lightbox modal).
 *
 * The footer "Bilde slettes om {N} dager" appears only when
 * retainsLeftDays is provided (admin viewer of an approved instance —
 * see CHORE-10 30-day retention contract).
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface PhotoProofThumbProps {
  url: string;
  alt: string;
  /**
   * Pixel size of the square thumbnail. Default 88 (mobile). `'wide'` fyller
   * bredden med 128 px høyde, som bevisbildet i Godkjenn-kortet (#155).
   */
  size?: 88 | 120 | 'wide';
  /** Optional click handler — when set, the thumb becomes a button-like
   *  trigger that opens the lightbox. */
  onOpen?: () => void;
  /** Days remaining before the proof file is deleted by the retention
   *  cron (Plan 02-03 cleanupProofs). Render the footer when set. */
  retainsLeftDays?: number;
  className?: string;
}

export function PhotoProofThumb({
  url,
  alt,
  size = 88,
  onOpen,
  retainsLeftDays,
  className,
}: PhotoProofThumbProps): ReactNode {
  const { t } = useTranslation();
  const wide = size === 'wide';
  const dim = wide ? undefined : `${size}px`;
  const interactive = typeof onOpen === 'function';

  const img = (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      style={dim ? { width: dim, height: dim } : undefined}
      className={wide ? 'h-32 w-full rounded-row object-cover' : 'rounded-md object-cover'}
    />
  );

  return (
    <div
      data-testid="photo-proof-thumb"
      className={[wide ? 'flex flex-col gap-1' : 'inline-flex flex-col gap-1', className]
        .filter(Boolean)
        .join(' ')}
    >
      {interactive ? (
        <button
          type="button"
          data-testid="photo-proof-thumb-button"
          onClick={onOpen}
          aria-label={alt}
          className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {img}
        </button>
      ) : (
        img
      )}
      {typeof retainsLeftDays === 'number' ? (
        <p className="text-ink-2 text-xs leading-tight">
          {t('photos.retentionFooter', { count: retainsLeftDays })}
        </p>
      ) : null}
    </div>
  );
}
